import React, {
    type ChangeEvent,
    type ClipboardEvent,
    type FormEvent,
    type KeyboardEvent,
    useRef,
} from "react";

interface GlobalTextInputFieldProps {
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    label?: string;
    className?: string;
    title?: string;
    type?: string;
    fontSize?: string;
    fontWeight?: string;
    leadingElement?: React.ReactNode;
    trailingElement?: React.ReactNode;
    trailingElementOnClick?: () => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    readonly?: boolean;
    borderColor?: string;
    is2FA?: boolean;
    showSearchResults?: boolean;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    prefixIcon?: React.ReactNode;
    prefixIconPadding?: string;
    suffixIcon?: React.ReactNode;
    suffixIconOnClick?: () => void;
    allowDecimal?: boolean;
    full?: boolean;
    telegram?: boolean;
    inputBorder?: string;
    disabled?: boolean;
    showBorder?: boolean;
    backgroundColor?: string;
    suffixIconPadding?: string;
}

const GlobalTextInputField: React.FC<GlobalTextInputFieldProps> = ({
    value,
    onChange,
    placeholder = "",
    className = "",
    title = "",
    type = "text",
    label,
    leadingElement,
    trailingElement,
    borderColor, // (kept for compatibility)
    fontSize = "12px",
    fontWeight = "font-normal",
    onKeyDown,
    trailingElementOnClick,
    onSubmit,
    disabled,
    readonly = false,
    is2FA = false,
    showSearchResults = false,
    onClick,
    prefixIcon,
    prefixIconPadding = "pl-[40px]",
    suffixIcon,
    suffixIconOnClick,
    allowDecimal = true,
    full = false,
    telegram = false,
    inputBorder,
    showBorder = true,
    backgroundColor = "bg-transparent",
    suffixIconPadding = "pr-4",
    ...props
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        const isAllowedKey = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "v"].includes(
            key
        );

        if (type === "number") {
            if (key === "ArrowUp" || key === "ArrowDown" || key === "e" || key === "+" || key === "-") {
                e.preventDefault();
            }
            if (!allowDecimal && key === ".") {
                e.preventDefault();
            }
        }

        if (is2FA) {
            const inputValue = (e.target as HTMLInputElement).value;
            if (inputValue.length >= 6 && !isAllowedKey) e.preventDefault();
            if (!key.match(/[0-9]/) && !isAllowedKey) e.preventDefault();
        }

        if (key === "Enter") e.preventDefault();

        onKeyDown?.(e);
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
        e.preventDefault();
        e.stopPropagation();
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;

        if (type === "number") {
            if (allowDecimal) {
                const regex = /^\d*(\.\d*)?$/;
                if (!regex.test(inputValue)) {
                    inputValue = value;
                }
            } else {
                inputValue = inputValue.replace(/[^0-9]/g, "");
                if (inputRef.current) {
                    inputRef.current.value = inputValue;
                }
            }
        }

        onChange({
            ...e,
            target: {
                ...e.target,
                value: inputValue,
            },
        });
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        if (type === "number" || is2FA) {
            e.preventDefault();
            const pasteData = e.clipboardData.getData("Text");
            let filteredData = pasteData;

            if (type === "number") {
                filteredData = allowDecimal ? pasteData.replace(/[^0-9.]/g, "") : pasteData.replace(/[^0-9]/g, "");
                if (allowDecimal) {
                    const parts = filteredData.split(".");
                    if (parts.length > 2) filteredData = parts[0] + "." + parts.slice(1).join("");
                }
            } else if (is2FA) {
                filteredData = pasteData.replace(/[^0-9]/g, "").slice(0, 6);
            }

            if (inputRef.current) {
                const input = inputRef.current;
                const currentValue = input.value;
                const selectionStart = input.selectionStart || 0;
                const selectionEnd = input.selectionEnd || 0;
                const newValue =
                    currentValue.substring(0, selectionStart) +
                    filteredData +
                    currentValue.substring(selectionEnd);

                const finalValue = is2FA ? newValue.slice(0, 6) : newValue;
                input.value = finalValue;
                onChange({ target: { value: finalValue } } as ChangeEvent<HTMLInputElement>);
            }
        }
    };

    const borderClasses = showSearchResults
        ? "border-t-bg-button-primary border-l-bg-button-primary border-r-bg-button-primary border-b-border-subtle"
        : inputBorder
            ? inputBorder
            : "border-border-subtle rounded-b-lg";

    // Force disabled look/behavior without changing your layout
    const computedBg = disabled ? "bg-bg-base" : backgroundColor;
    const iconInteractivity = disabled ? "pointer-events-none" : "cursor-pointer";

    return (
        <div className={`flex flex-col ${className}`}>
            {title && (
                <>
                    <span className="text-text-base text-[10px] font-semibold">{title}</span>
                    <div className="my-[3px]" />
                </>
            )}
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit?.(e);
                }}
            >
                {label && <label className="text-text-base text-[10px] font-semibold">{label}</label>}
                <div className="relative flex items-center">
                    {leadingElement && (
                        <div className="absolute inset-y-0 left-0 flex items-center">
                            {typeof leadingElement === "string" ? <img src={leadingElement} alt="" /> : leadingElement}
                        </div>
                    )}

                    {prefixIcon && (
                        <div className="absolute inset-y-0 left-0 flex items-center pl-[6px] h-full">
                            {prefixIcon}
                        </div>
                    )}

                    <input
                        type={type}
                        placeholder={placeholder}
                        className={`w-full px-2 py-[10px] text-text-base text-[${fontSize}] ${fontWeight} rounded-t-lg ${computedBg} ${showBorder ? `border ${borderClasses}` : ""
                            } focus:outline-none placeholder:text-text-header ${leadingElement || prefixIcon ? `${prefixIconPadding}` : ""
                            } ${suffixIcon ? "pr-[46px]" : ""} ${type === "number" ? "no-spinner" : ""} ${telegram ? "pl-[80px]" : ""
                            } ${disabled ? "cursor-not-allowed" : ""}`}
                        value={value}
                        ref={inputRef}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onChange={handleInputChange}
                        readOnly={readonly}
                        onClick={onClick}
                        onWheel={handleWheel}
                        pattern={type === "number" && allowDecimal ? "^\\d*(\\.\\d{0,})?$" : undefined}
                        inputMode={type === "number" ? (allowDecimal ? "decimal" : "numeric") : undefined}
                        aria-disabled={disabled || undefined}
                        disabled={disabled}
                        style={{ outline: "none" }}
                        {...props}
                    />

                    {trailingElement && (
                        <span
                            className={`absolute inset-y-0 right-0 flex items-center pr-4 text-text-base font-semibold text-[12px] ${iconInteractivity}`}
                            onClick={disabled ? undefined : trailingElementOnClick}
                        >
                            {trailingElement}
                        </span>
                    )}

                    {suffixIcon && (
                        <span
                            className={`absolute inset-y-0 right-0 flex items-center ${suffixIconPadding} ${iconInteractivity}`}
                            onClick={disabled ? undefined : suffixIconOnClick}
                        >
                            {suffixIcon}
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
};

export default GlobalTextInputField;
